import { useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { fbTrack } from "@/lib/metaPixel";
import { toast } from "sonner";
import { useI18n } from "@/i18n/I18nContext";

function fmt(d) { if (!d) return ""; const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const da = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${da}`; }
function nightsBetween(a, b) { if (!a || !b) return 0; return Math.round((b - a) / (1000 * 60 * 60 * 24)); }

// Return the nightly rate that applies on a given Date, matching backend logic:
// the season with the latest start_date that covers the night wins; otherwise default.
function priceForDate(date, villa) {
  if (!date || !villa) return null;
  const iso = fmt(date);
  const seasons = villa.pricing_seasons || [];
  const covering = seasons.filter(s => iso >= s.start_date && iso < s.end_date);
  if (covering.length) {
    const best = covering.reduce((a, b) => (b.start_date > a.start_date ? b : a));
    return Math.round(Number(best.nightly_rate));
  }
  return Math.round(Number(villa.nightly_rate));
}

export default function Booking() {
  const { t, lang } = useI18n();
  const [villa, setVilla] = useState(null);
  const [unavailable, setUnavailable] = useState([]);
  // Track viewport width so the calendar renders 1 month on mobile, 2 on desktop.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 640 : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [range, setRange] = useState({ from: undefined, to: undefined });
  const [guests, setGuests] = useState(2);
  const [quote, setQuote] = useState(null);
  const [quoteErr, setQuoteErr] = useState("");
  const [form, setForm] = useState({ guest_name: "", guest_email: "", guest_phone: "", notes: "" });
  const [referralInput, setReferralInput] = useState("");
  const [referral, setReferral] = useState(null); // { code, discount_eur, owner_name } when valid
  const [referralErr, setReferralErr] = useState("");
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    api.get("/villa").then(r => setVilla(r.data));
    api.get("/availability").then(r => setUnavailable(r.data.unavailable));
    // Meta Pixel — someone reached the booking flow. Optimising ad delivery
    // for this event surfaces high-intent travellers.
    fbTrack("InitiateCheckout");

    // Deep-link support: /booking?checkin=YYYY-MM-DD&checkout=YYYY-MM-DD&guests=2&code=HOLD-XXXX
    try {
      const p = new URLSearchParams(window.location.search);
      const ci = p.get("checkin");
      const co = p.get("checkout");
      const g = p.get("guests");
      const c = p.get("code");
      if (ci && co) {
        const [y1,m1,d1] = ci.split("-").map(Number);
        const [y2,m2,d2] = co.split("-").map(Number);
        if (y1 && y2) setRange({ from: new Date(y1, m1-1, d1), to: new Date(y2, m2-1, d2) });
      }
      if (g) { const n = parseInt(g, 10); if (n >= 1 && n <= 12) setGuests(n); }
      if (c) setReferralInput(c.toUpperCase());
    } catch {}
  }, []);

  const disabledDays = useMemo(() => {
    const days = unavailable.map(s => { const [y,m,d] = s.split("-").map(Number); return new Date(y, m-1, d); });
    return [{ before: new Date() }, ...days];
  }, [unavailable]);

  const nights = nightsBetween(range.from, range.to);

  // Does the currently selected range overlap any unavailable date?
  const rangeUnavailable = useMemo(() => {
    if (!range.from || !range.to || !unavailable?.length) return false;
    const set = new Set(unavailable);
    let d = new Date(range.from);
    const end = new Date(range.to);
    while (d < end) {
      if (set.has(fmt(d))) return true;
      d = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    }
    return false;
  }, [range, unavailable]);

  useEffect(() => {
    setQuoteErr(""); setQuote(null);
    if (!range.from || !range.to || !villa) return;
    let ignore = false;
    api.post("/quote", {
      checkin: fmt(range.from),
      checkout: fmt(range.to),
      guests,
      referral_code: referral?.code,
    })
      .then(r => { if (!ignore) setQuote(r.data); })
      .catch(e => { if (!ignore) setQuoteErr(e.response?.data?.detail || "Unable to price these dates."); });
    return () => { ignore = true; };
  }, [range, guests, villa, referral]);

  async function applyReferral(overrides = {}) {
    const code = (overrides.code || referralInput || "").trim().toUpperCase();
    if (!code) { setReferralErr("Please enter a referral code."); return; }
    setReferralErr("");
    try {
      const payload = { code };
      if (range.from && range.to) {
        payload.checkin = fmt(range.from);
        payload.checkout = fmt(range.to);
      }
      // Include email if the guest has typed one — required for restricted rescue /
      // welcome-back codes so the UI can display the discount before checkout.
      const email = (form.guest_email || "").trim().toLowerCase();
      if (email) payload.guest_email = email;
      const { data } = await api.post("/referral/validate", payload);
      setReferral(data);
      if (!overrides.silent) toast.success(`Applied — €${Math.round(data.discount_eur)} off`);
    } catch (e) {
      setReferral(null);
      if (!overrides.silent) setReferralErr(e.response?.data?.detail || "Referral code not valid.");
    }
  }
  function clearReferral() { setReferral(null); setReferralInput(""); setReferralErr(""); }

  // Auto-apply a rescue/referral code that arrived via URL as soon as we know the dates
  // (and re-try silently once the guest fills in their email — restricted codes need it).
  useEffect(() => {
    if (referralInput && !referral && range.from && range.to) {
      applyReferral({ code: referralInput, silent: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.from, range.to, form.guest_email]);

  async function reserve() {
    if (!quote || !range.from || !range.to) return;
    if (!form.guest_name || !form.guest_email || !form.guest_phone) { toast.error("Please provide your name, email and phone."); return; }
    setProcessing(true);
    // Forward whatever code the guest typed OR deep-linked, even if the pre-validate
    // step could not confirm it (restricted rescue/welcome-back codes require the
    // guest email, which we only have at reserve() time — checkout will validate strictly).
    const codeForCheckout = (referral?.code || referralInput || "").trim().toUpperCase() || undefined;
    try {
      const { data } = await api.post("/bookings/checkout", {
        checkin: fmt(range.from), checkout: fmt(range.to), guests,
        origin_url: window.location.origin,
        referral_code: codeForCheckout,
        ...form,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Unable to start checkout.");
      setProcessing(false);
    }
  }

  const minN = villa?.min_nights ?? 7;

  return (
    <>
      <Nav />
      <div className="pt-28 md:pt-32 pb-24 max-w-[1400px] mx-auto px-5 sm:px-6 md:px-16">
        <div className="overline">{t("booking.overline")}</div>
        <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl font-light mt-4 leading-[1.02] md:leading-none">{t("booking.heading")}</h1>
        <p className="mt-4 text-ionian/70 max-w-2xl text-sm md:text-base">{t("booking.sub", { n: minN })}</p>

        <div className="mt-10 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-8">
            <div className="border border-soft_border bg-white p-4 sm:p-6 md:p-10 overflow-x-auto">
              <div className="overline mb-4">{t("booking.select_stay")}</div>
              <DayPicker
                mode="range"
                numberOfMonths={isMobile ? 1 : 2}
                selected={range}
                onSelect={setRange}
                disabled={disabledDays}
                data-testid="booking-calendar"
                styles={{
                  caption: { fontFamily: "Cormorant Garamond, serif", fontSize: "1.25rem", color: "#0A192F" },
                  head: { fontFamily: "Outfit", fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.2em", color: "#B89F73" },
                  day: { height: "3rem", width: "3rem" },
                }}
                modifiersStyles={{
                  selected: { backgroundColor: "#0A192F", color: "#F9F9F6", borderRadius: 0 },
                  range_middle: { backgroundColor: "#EBE6DD", color: "#0A192F", borderRadius: 0 },
                  disabled: { color: "#c5c1b7", textDecoration: "line-through" },
                }}
                components={{
                  DayContent: ({ date, activeModifiers }) => {
                    const rate = priceForDate(date, villa);
                    const isDisabled = activeModifiers && activeModifiers.disabled;
                    return (
                      <div className="flex flex-col items-center justify-center leading-tight" data-testid={`day-${fmt(date)}`}>
                        <span className="text-sm">{date.getDate()}</span>
                        {rate && !isDisabled && (
                          <span data-testid={`price-${fmt(date)}`} className="text-[9px] mt-0.5 tracking-wide opacity-70">
                            €{rate}
                          </span>
                        )}
                      </div>
                    );
                  }
                }}
              />
              <div className="hairline mt-8 pt-6 grid grid-cols-2 md:grid-cols-4 gap-6 text-sm">
                <div><div className="overline">{t("booking.checkin")}</div><div data-testid="booking-checkin-value" className="font-serif text-xl mt-1">{range.from ? fmt(range.from) : "—"}</div></div>
                <div><div className="overline">{t("booking.checkout")}</div><div data-testid="booking-checkout-value" className="font-serif text-xl mt-1">{range.to ? fmt(range.to) : "—"}</div></div>
                <div><div className="overline">{t("booking.nights")}</div><div data-testid="booking-nights" className="font-serif text-xl mt-1">{nights || "—"}</div></div>
                <div>
                  <div className="overline">{t("booking.guests")}</div>
                  <select data-testid="booking-guests" value={guests} onChange={e=>setGuests(Number(e.target.value))} className="mt-1 bg-transparent border-b border-soft_border font-serif text-xl w-full">
                    {Array.from({length: villa?.max_guests ?? 4}, (_,i)=>i+1).map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-12 border border-soft_border bg-white p-6 md:p-10">
              <div className="overline mb-6">{t("booking.guest_details")}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div><div className="overline mb-1">{t("booking.full_name")}</div>
                  <input data-testid="booking-name" required value={form.guest_name} onChange={e=>setForm({...form,guest_name:e.target.value})} className="editorial-input w-full" /></div>
                <div><div className="overline mb-1">{t("booking.email")}</div>
                  <input data-testid="booking-email" required type="email" value={form.guest_email} onChange={e=>setForm({...form,guest_email:e.target.value})} className="editorial-input w-full" /></div>
                <div><div className="overline mb-1">{t("booking.phone")}</div>
                  <input data-testid="booking-phone" required value={form.guest_phone} onChange={e=>setForm({...form,guest_phone:e.target.value})} className="editorial-input w-full" /></div>
                <div><div className="overline mb-1">{t("booking.notes")}</div>
                  <input data-testid="booking-notes" value={form.notes} onChange={e=>setForm({...form,notes:e.target.value})} className="editorial-input w-full" /></div>
              </div>
            </div>

            {rangeUnavailable && range.from && range.to && (
              <WaitlistForm checkin={fmt(range.from)} checkout={fmt(range.to)} defaultGuests={guests} />
            )}
          </div>

          <aside className="lg:col-span-4">
            <div className="border border-soft_border bg-white p-8 sticky top-28">
              <div className="overline">{t("booking.summary")}</div>
              <div className="font-serif text-3xl mt-4">{villa?.villa_name ?? "Ithaca Garden Villa"}</div>
              <div className="text-sm text-ionian/60 mt-1">{villa?.location}</div>

              <div className="hairline mt-6 pt-6 space-y-3 text-sm">
                {quote ? (
                  <>
                    <Row label={`€${quote.nightly_rate} × ${quote.nights} ${t("booking.nights").toLowerCase()}`} value={`€${quote.subtotal.toFixed(2)}`} />
                    <Row label={t("booking.cleaning_fee")} value={`€${quote.cleaning_fee.toFixed(2)}`} />
                    {quote.deposit > 0 && (
                      <Row label={t("booking.deposit")} value={`€${quote.deposit.toFixed(2)}`} />
                    )}
                    {quote.discount > 0 && (
                      <Row label={`${t("booking.referral_line")} (${referral?.code})`} value={`−€${quote.discount.toFixed(2)}`} />
                    )}
                    <div className="hairline pt-4 flex justify-between font-serif text-2xl">
                      <span>{t("booking.total")}</span><span data-testid="booking-total">€{quote.total.toFixed(2)}</span>
                    </div>
                    {quote.deposit > 0 && (
                      <div data-testid="deposit-note" className="text-xs text-ink/55 italic mt-3 leading-relaxed">{t("booking.deposit_note")}</div>
                    )}
                  </>
                ) : quoteErr ? (
                  <div data-testid="booking-quote-error" className="text-terracotta text-sm">{quoteErr}</div>
                ) : (
                  <div className="text-ionian/50 text-sm italic">{t("booking.select_prompt")}</div>
                )}
              </div>

              {/* Referral code */}
              <div className="mt-6 pt-6 border-t border-soft_border">
                <div className="overline mb-2">{t("booking.referral_title")}</div>
                {referral ? (
                  <div className="flex items-center justify-between text-sm">
                    <div>
                      <span className="font-mono text-brass mr-2">{referral.code}</span>
                      <span className="text-ionian/60">− €{Math.round(referral.discount_eur)} {t("booking.referral_off")}</span>
                    </div>
                    <button
                      type="button"
                      data-testid="referral-clear"
                      onClick={clearReferral}
                      className="text-xs text-terracotta underline underline-offset-2"
                    >{t("booking.referral_remove")}</button>
                  </div>
                ) : (
                  <div>
                    <div className="flex gap-2">
                      <input
                        data-testid="referral-input"
                        value={referralInput}
                        onChange={e => setReferralInput(e.target.value.toUpperCase())}
                        placeholder={t("booking.referral_placeholder")}
                        className="editorial-input flex-1 font-mono text-sm"
                      />
                      <button
                        type="button"
                        data-testid="referral-apply"
                        onClick={applyReferral}
                        className="px-4 py-2 border border-ionian text-ionian text-xs uppercase tracking-widest hover:bg-ionian hover:text-stone_white transition"
                      >{t("booking.referral_apply")}</button>
                    </div>
                    {referralErr && <div data-testid="referral-error" className="text-terracotta text-xs mt-2">{referralErr}</div>}
                    <div className="text-xs text-ionian/50 mt-2 italic">{t("booking.referral_hint")}</div>
                  </div>
                )}
              </div>

              <button data-testid="booking-reserve-btn" onClick={reserve} disabled={!quote || processing}
                className="btn-brass w-full mt-8 py-4 rounded-full text-sm uppercase tracking-widest disabled:opacity-50">
                {processing ? t("booking.redirecting") : t("booking.pay")}
              </button>
              <div className="text-xs text-ionian/50 mt-4 text-center">{t("booking.secured")}</div>
            </div>
          </aside>
        </div>
      </div>
      <Footer />
    </>
  );
}

function Row({ label, value }) {
  return <div className="flex justify-between text-ionian/80"><span>{label}</span><span>{value}</span></div>;
}

function WaitlistForm({ checkin, checkout, defaultGuests }) {
  const { t, lang } = useI18n();
  const [f, setF] = useState({ name: "", email: "", phone: "", notes: "", flexibility_days: 3 });
  const [state, setState] = useState("idle"); // idle | submitting | done | error
  const [msg, setMsg] = useState("");

  async function submit(e) {
    e.preventDefault();
    if (!f.name || !f.email) { setMsg(t("waitlist.error")); setState("error"); return; }
    setState("submitting"); setMsg("");
    try {
      const { data } = await api.post("/waitlist", {
        email: f.email.trim().toLowerCase(),
        name: f.name.trim(),
        phone: f.phone.trim(),
        guests: defaultGuests,
        checkin, checkout,
        flexibility_days: Number(f.flexibility_days) || 0,
        notes: f.notes.trim(),
        language: lang || "en",
      });
      setState("done");
      setMsg(data.already ? t("waitlist.already") : t("waitlist.success"));
    } catch (e2) {
      setState("error");
      setMsg(e2.response?.data?.detail || t("waitlist.error"));
    }
  }

  return (
    <div className="mt-12 border border-terracotta/40 bg-cream p-6 md:p-10" data-testid="waitlist-panel">
      <div className="overline text-terracotta">{t("waitlist.overline")}</div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mt-3">
        <div>
          <h3 className="font-serif text-3xl md:text-4xl leading-tight">{t("waitlist.heading")}</h3>
          <p className="text-sm text-ionian/70 mt-3 max-w-2xl leading-relaxed">{t("waitlist.body")}</p>
        </div>
        <div className="text-xs text-ionian/60 font-mono">{checkin} → {checkout}</div>
      </div>

      {state === "done" ? (
        <div data-testid="waitlist-success" className="mt-6 border-t border-soft_border pt-6 text-ionian">
          <div className="font-serif text-xl">{msg}</div>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="waitlist-form">
          <div>
            <div className="overline mb-1">{t("waitlist.name_label")}</div>
            <input required data-testid="waitlist-name" value={f.name} onChange={e => setF({...f, name: e.target.value})} className="editorial-input w-full" placeholder={t("waitlist.name_placeholder")} />
          </div>
          <div>
            <div className="overline mb-1">{t("waitlist.email_label")}</div>
            <input required type="email" data-testid="waitlist-email" value={f.email} onChange={e => setF({...f, email: e.target.value})} className="editorial-input w-full" placeholder={t("waitlist.email_placeholder")} />
          </div>
          <div>
            <div className="overline mb-1">{t("waitlist.phone_label")}</div>
            <input data-testid="waitlist-phone" value={f.phone} onChange={e => setF({...f, phone: e.target.value})} className="editorial-input w-full" placeholder={t("waitlist.phone_placeholder")} />
          </div>
          <div>
            <div className="overline mb-1">{t("waitlist.flex_label")}</div>
            <select data-testid="waitlist-flex" value={f.flexibility_days} onChange={e => setF({...f, flexibility_days: e.target.value})} className="editorial-input w-full">
              <option value={0}>{t("waitlist.flex_none")}</option>
              <option value={3}>{t("waitlist.flex_3")}</option>
              <option value={7}>{t("waitlist.flex_7")}</option>
            </select>
          </div>
          <div className="md:col-span-2">
            <div className="overline mb-1">{t("waitlist.notes_label")}</div>
            <input data-testid="waitlist-notes" value={f.notes} onChange={e => setF({...f, notes: e.target.value})} className="editorial-input w-full" placeholder={t("waitlist.notes_placeholder")} />
          </div>
          {msg && state === "error" && (
            <div data-testid="waitlist-error" className="md:col-span-2 text-terracotta text-sm">{msg}</div>
          )}
          <div className="md:col-span-2">
            <button type="submit" disabled={state === "submitting"} data-testid="waitlist-submit" className="px-6 py-3 bg-ionian text-stone_white text-xs uppercase tracking-widest hover:bg-ionian/90 transition disabled:opacity-60">
              {state === "submitting" ? "…" : t("waitlist.submit")}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

